<template>
    <div class="flex flex-col gap-1" :class="placement === 'sidebar' ? 'w-full' : 'w-screen md:w-full md:max-w-(--breakpoint-md) md:mx-auto shrink-0 mb-2 px-2 items-center'">
        <BaseButton
            theme="secondary"
            :class="{ 'w-full': placement === 'sidebar' }"
            data-testid="draft-manager-review"
            :disabled="inFlight"
            class="flex items-center justify-center gap-2"
            @click="handleClick(false)">
            <LoadingSpinner v-if="loading" class="h-4" />
            <i v-else class="bi bi-workspace" aria-hidden="true"></i>
            {{ t("chat.managerReview.draftButton", { name: draftButtonName }) }}
        </BaseButton>
        <div v-if="noticeMessage" class="body-small text-notification-error-text" role="alert">{{ noticeMessage }}</div>

        <ChatManagerReviewDialog
            v-model:open="dialogOpen"
            :draft="draft"
            :reviewee-name="revieweeName"
            :message-id="latestMessageId"
            :rating-options="ratingOptions"
            :push-confirmation-note="pushConfirmationNote"
            :redrafting="inFlight"
            :redraft-error="redraftError"
            @redraft="handleClick(true)" />
    </div>
</template>

<script setup lang="ts">
import { ChatAction } from "~vue/chatActions";
import BaseButton from "~vue/components/BaseButton.vue";
import { useActionHandler } from "~vue/composables/useActionHandler";
import { useDeferredLoading } from "~vue/composables/useDeferredLoading";
import LoadingSpinner from "~vue/icons/LoadingSpinner.vue";
import { PERFORMANCE_REVIEW_EVENT_PREFIX } from "~vue/Moments/constants";
import { resolveActionRefusal } from "~vue/utils/actionRefusal";
import { logError, logUserInteraction } from "~vue/utils/logUtils";
import { computed, inject, ref } from "vue";
import { useI18n } from "vue-i18n";

import ChatManagerReviewDialog, { type RatingScope } from "./ChatManagerReviewDialog.vue";

interface CardDraft {
    review_ref: string;
    overall_comment: string;
    overall_rating: string;
    loaded_from_workday?: boolean;
    confidential_overlap?: string[];
    sections: Array<{
        section_id: string;
        label: string;
        comment: string;
        rating: string;
        rating_scope: string;
    }>;
}

interface Props {
    latestMessageId: number;
    /** ``sidebar`` fills the performance chat's right-hand panel; ``chat`` sits above the composer. */
    placement?: "chat" | "sidebar";
    /** The reviewee's name from the page context, so the label names them before a draft exists. */
    subjectName?: string | null;
}

const props = withDefaults(defineProps<Props>(), { placement: "chat" });

const { t } = useI18n();
const { execute } = useActionHandler();
const coachingSessionUuid = inject<string | undefined>("coachingSessionUuid", undefined);
const { loading, start: startLoading, stop: stopLoading } = useDeferredLoading();

const noticeMessage = ref("");
// A redraft is asked for from inside the open dialog, so its failure is shown there, not behind it.
const redraftError = ref("");

function showError(redraft: boolean, message: string) {
    if (redraft) {
        redraftError.value = message;
    } else {
        noticeMessage.value = message;
    }
}
// Not `loading`: that one is deferred by 200ms for flicker, and the dialog must block submit from the
// moment a redraft starts.
const inFlight = ref(false);

const dialogOpen = ref(false);
const draft = ref<CardDraft | null>(null);
const revieweeName = ref<string | null>(null);
// Server-authored per-scope rating capability, resolved from the same config the submit will be
// validated against -- so the manager is never offered a rating the push would then refuse.
const ratingOptions = ref<RatingScope[]>([]);
// The connection's post-submit note for the dialog's submitted step (LAB-2202); "" is the default.
const pushConfirmationNote = ref("");

// The label names the reviewee; once a draft lands, its server-resolved name wins over the page prop.
const draftButtonName = computed(() => revieweeName.value || props.subjectName || t("chat.managerReview.draftButtonFallbackName"));

// ``redraft`` drafts every comment from the conversation instead of keeping what is already in
// the manager's Workday review.
async function handleClick(redraft: boolean) {
    // ``loading`` turns on only after 200ms, so a quick second click would otherwise start a second draft.
    if (inFlight.value) {
        return;
    }
    startLoading();
    inFlight.value = true;
    noticeMessage.value = "";
    redraftError.value = "";
    const event = redraft ? "redraft_manager_review_clicked" : "draft_manager_review_clicked";
    logUserInteraction(`${PERFORMANCE_REVIEW_EVENT_PREFIX}${event}`, { session_id: coachingSessionUuid }, coachingSessionUuid);

    try {
        const result = await resolveActionRefusal(execute(ChatAction.DRAFT_MANAGER_REVIEW_CARD, { message_id: props.latestMessageId, redraft }, { sync: true }));
        if (result?.success && result.payload?.draft) {
            draft.value = result.payload.draft;
            revieweeName.value = result.payload.reviewee_name ?? null;
            ratingOptions.value = result.payload.rating_options ?? [];
            pushConfirmationNote.value = result.payload.push_confirmation_note ?? "";
            dialogOpen.value = true;
        } else {
            showError(redraft, result?.payload?.error ?? t("chat.managerReview.draftError"));
        }
    } catch (e) {
        logError(e);
        showError(redraft, t("chat.managerReview.draftError"));
    } finally {
        stopLoading();
        inFlight.value = false;
    }
}
</script>
